import type {
  Card,
  CardNumber,
  CardSpec,
  Color,
  Constants,
  Face,
  MatchConfig,
  Side,
} from './types';

export const COLORS: readonly Color[] = ['R', 'B', 'G', 'Y'];
export const NUMBERS: readonly CardNumber[] = [1, 2, 3];

/** The 12 faces in canonical order: R1 R2 R3 B1 … Y3. */
export const ALL_FACES: readonly Face[] = Object.freeze(
  COLORS.flatMap((color) => NUMBERS.map((number) => Object.freeze({ color, number }))),
);

export const isColor = (v: unknown): v is Color => v === 'R' || v === 'B' || v === 'G' || v === 'Y';
export const isCardNumber = (v: unknown): v is CardNumber => v === 1 || v === 2 || v === 3;

export function isFace(v: unknown): v is Face {
  if (typeof v !== 'object' || v === null) return false;
  const f = v as { color?: unknown; number?: unknown };
  return isColor(f.color) && isCardNumber(f.number);
}

export const sameFace = (a: Face, b: Face): boolean => a.color === b.color && a.number === b.number;

/** A face matches the top if it has the same color or the same number (§7.3). */
export const faceMatches = (face: Face, top: Face | null): boolean =>
  top === null || face.color === top.color || face.number === top.number;

export const faceCode = (f: Face): string => `${f.color}${f.number}`;

export function parseFace(code: string): Face | null {
  const m = /^([RBGY])([123])$/.exec(code.trim().toUpperCase());
  if (!m) return null;
  return { color: m[1] as Color, number: Number(m[2]) as CardNumber };
}

/** The base deck (§3.4): DECK_COPIES_PER_FACE of each face plus WILDS_PER_DECK Wilds. */
export function baseDeckSpec(constants: Constants): CardSpec[] {
  const specs: CardSpec[] = [];
  for (const face of ALL_FACES)
    for (let i = 0; i < constants.DECK_COPIES_PER_FACE; i++) specs.push({ kind: 'normal', face });
  for (let i = 0; i < constants.WILDS_PER_DECK; i++) specs.push({ kind: 'wild' });
  return specs;
}

export function deckSpecFor(config: MatchConfig, side: Side): readonly CardSpec[] {
  return config.decks?.[side] ?? baseDeckSpec(config.constants);
}

const copyLabel = (index: number): string => {
  const letter = String.fromCharCode(97 + (index % 26));
  return index < 26 ? letter : `${letter}${Math.floor(index / 26)}`;
};

/**
 * Builds the side's match cards with ids `<side>-<face>-<copy>` (§3.4), e.g. S-R1-a, S-W-b.
 * Ids depend only on the deck spec, so every client can derive them from the public config.
 */
export function buildCards(config: MatchConfig, side: Side): Card[] {
  const counters = new Map<string, number>();
  return deckSpecFor(config, side).map((spec) => {
    const code = spec.kind === 'wild' ? 'W' : faceCode(spec.face);
    const n = counters.get(code) ?? 0;
    counters.set(code, n + 1);
    const id = `${side}-${code}-${copyLabel(n)}`;
    // Long Night: a card keeps its Stamp, Finish and growing value.
    const extras = {
      ...(spec.stamp !== undefined ? { stamp: spec.stamp } : {}),
      ...(spec.finish !== undefined ? { finish: spec.finish } : {}),
      ...(spec.value !== undefined ? { value: spec.value } : {}),
      ...(spec.iou ? { iou: true } : {}),
      ...(spec.trick !== undefined ? { trick: spec.trick } : {}),
    };
    return spec.kind === 'wild'
      ? Object.freeze({ id, owner: side, kind: 'wild' as const, ...extras })
      : Object.freeze({
          id,
          owner: side,
          kind: 'normal' as const,
          face: Object.freeze({ ...spec.face }),
          ...extras,
        });
  });
}

/** The index in the side's deck spec of each card id (Long Night: changes go back to the run deck). */
export function cardSpecIndex(config: MatchConfig, side: Side): Record<string, number> {
  const out: Record<string, number> = {};
  buildCards(config, side).forEach((c, i) => (out[c.id] = i));
  return out;
}

export const cardCode = (card: Card): string =>
  card.kind === 'wild' ? 'W' : faceCode(card.face as Face);
